/**
 * Postinstall script — runs automatically after `npm install -g @sibrik/editor`.
 *
 * Finds the Claude Desktop config file and adds the sibrik-editor MCP server
 * entry if it's not already present. This way the user doesn't need to
 * manually edit JSON — just install the package and restart Claude Desktop.
 */

import { promises as fs } from 'node:fs';
import path from 'node:path';
import os from 'node:os';

const SERVER_KEY = 'sibrik-editor';

function getConfigPath(): string | null {
  const platform = process.platform;
  const home = os.homedir();

  if (platform === 'darwin') {
    return path.join(home, 'Library', 'Application Support', 'Claude', 'claude_desktop_config.json');
  }
  if (platform === 'win32') {
    const appData = process.env.APPDATA || path.join(home, 'AppData', 'Roaming');
    return path.join(appData, 'Claude', 'claude_desktop_config.json');
  }
  if (platform === 'linux') {
    const configHome = process.env.XDG_CONFIG_HOME || path.join(home, '.config');
    return path.join(configHome, 'Claude', 'claude_desktop_config.json');
  }
  return null;
}

async function main() {
  const configPath = getConfigPath();
  if (!configPath) {
    console.log('[sibrik-editor] Unsupported platform — skip auto-config.');
    console.log('[sibrik-editor] Please add the MCP server manually to your Claude Desktop config:');
    console.log(JSON.stringify({
      mcpServers: {
        [SERVER_KEY]: {
          command: 'npx',
          args: ['-y', '@sibrik/editor'],
        },
      },
    }, null, 2));
    return;
  }

  let config: any = { mcpServers: {} };

  try {
    const raw = await fs.readFile(configPath, 'utf-8');
    config = JSON.parse(raw);
    if (!config.mcpServers || typeof config.mcpServers !== 'object') {
      config.mcpServers = {};
    }
  } catch {
    // Config doesn't exist yet — we'll create it
    const configDir = path.dirname(configPath);
    try {
      await fs.mkdir(configDir, { recursive: true });
    } catch {
      console.log(`[sibrik-editor] Could not create config directory: ${configDir}`);
      console.log('[sibrik-editor] Please add the MCP server manually:');
      console.log(JSON.stringify({
        mcpServers: {
          [SERVER_KEY]: {
            command: 'npx',
            args: ['-y', '@sibrik/editor'],
          },
        },
      }, null, 2));
      return;
    }
  }

  // Check if already present
  if (config.mcpServers[SERVER_KEY]) {
    console.log(`[sibrik-editor] Already registered in Claude Desktop config.`);
    console.log(`[sibrik-editor] Config: ${configPath}`);
    console.log(`[sibrik-editor] Restart Claude Desktop to use the editor.`);
    return;
  }

  // Add the editor MCP server
  config.mcpServers[SERVER_KEY] = {
    command: 'npx',
    args: ['-y', '@sibrik/editor'],
  };

  try {
    await fs.writeFile(configPath, JSON.stringify(config, null, 2), 'utf-8');
    console.log(`[sibrik-editor] Added to Claude Desktop config: ${configPath}`);
    console.log(`[sibrik-editor] Restart Claude Desktop to activate the video editor.`);
    console.log(`[sibrik-editor] You can then ask Claude to: generate videos, merge clips, add text, add music, render MP4.`);
  } catch (err) {
    console.log(`[sibrik-editor] Could not write config file: ${configPath}`);
    console.log('[sibrik-editor] Please add the MCP server manually:');
    console.log(JSON.stringify({
      mcpServers: {
        [SERVER_KEY]: {
          command: 'npx',
          args: ['-y', '@sibrik/editor'],
        },
      },
    }, null, 2));
  }
}

main().catch((err) => {
  // Don't fail the install — just warn
  console.warn(`[sibrik-editor] Postinstall warning: ${err instanceof Error ? err.message : String(err)}`);
  console.warn('[sibrik-editor] You may need to add the MCP server manually to your Claude Desktop config.');
});
